import { Badge, Box, Button, HStack, Skeleton, Stack, Text, Wrap } from "@chakra-ui/react";

/**
 * One choice from a shallow tree of filters.
 *
 * Deliberately single-select. Multi-select chips offer every combination of
 * their options and most combinations mean nothing: "core groups and
 * administration" is not a question anyone asks.
 *
 * There is no "all" chip. Everything is the state you are in before choosing
 * anything, so it gets no button of its own -- clicking the active filter again
 * clears it and returns there. A chip for it would be a button that is on
 * whenever nothing is on, which reads as a contradiction.
 *
 * The whole tree is always on screen: hiding a level until its parent is chosen
 * costs a click and, worse, conceals that the options exist at all. The counts
 * are there so someone can see there are four closed groups without going
 * looking for them.
 *
 * The label, where there is one, sits BESIDE the chips rather than above them.
 * Above, in the small caps that mark a section, it read as a heading for
 * everything below it -- filters, count and table alike -- when it only names
 * the chips. Inline it cannot be mistaken for one, and costs no vertical space.
 * Under a search box it is left out: both are plainly filters, and "Filter"
 * beside the second one only repeated it.
 *
 * The count is NOT part of this component: it belongs directly above the table
 * it describes, which is a long way from here once the filter block has its own
 * spacing.
 */
export type FilterNode<TKey extends string> = {
  key: TKey;
  label: string;
  count: number;
  /** Spelt out on hover, where the chip's label has to stay short. */
  description?: string;
  children?: ReadonlyArray<FilterNode<TKey>>;
};

function containsKey<TKey extends string>(node: FilterNode<TKey>, key: TKey): boolean {
  return node.key === key || (node.children ?? []).some((child) => containsKey(child, key));
}

function Chip<TKey extends string>({
  node,
  value,
  clearKey,
  onChange,
  loading,
}: {
  node: FilterNode<TKey>;
  value: TKey;
  clearKey: TKey;
  onChange: (key: TKey) => void;
  loading: boolean;
}) {
  // exact: this is the filter. branch: an ancestor of it -- marked so choosing
  // "Core" does not leave the rest of the tree looking untouched.
  const state = node.key === value ? "exact" : containsKey(node, value) ? "branch" : "off";

  return (
    <Button
      size="sm"
      variant={state === "exact" ? "solid" : state === "branch" ? "subtle" : "outline"}
      colorPalette={state === "off" ? "gray" : "blue"}
      title={node.description}
      // Clicking the active filter clears it, which is how "everything" is
      // reached without a chip standing for it.
      onClick={() => onChange(state === "exact" ? clearKey : node.key)}
      // Not while the counts are still coming: a zero then means "not yet".
      disabled={!loading && node.count === 0 && state === "off"}
    >
      {node.label}
      {loading ? (
        <Skeleton ml={2} height="4" width="5" />
      ) : (
        <Badge ml={2} size="sm" variant={state === "exact" ? "solid" : "subtle"}>
          {node.count}
        </Badge>
      )}
    </Button>
  );
}

/**
 * A child row, joined to its parent by an elbow.
 *
 * Drawn rather than indented: a wrapping chip row breaks wherever it runs out of
 * width, so padding alone leaves the first chip of every wrapped line looking
 * equally nested. The corner's height is fixed rather than proportional, so it
 * still meets the first chip when the row wraps to two lines, and it stops short
 * of the chip rather than running into it.
 */
function ChildRow<TKey extends string>({
  nodes,
  value,
  clearKey,
  onChange,
  loading,
}: {
  nodes: ReadonlyArray<FilterNode<TKey>>;
  value: TKey;
  clearKey: TKey;
  onChange: (key: TKey) => void;
  loading: boolean;
}) {
  return (
    <HStack align="stretch" gap={0} pl={4}>
      <Box position="relative" w="26px" flexShrink={0}>
        <Box
          position="absolute"
          left="0"
          top="-8px"
          h="24px"
          w="18px"
          borderLeftWidth="2px"
          borderBottomWidth="2px"
          borderColor="border"
          borderBottomLeftRadius="8px"
        />
      </Box>
      <Wrap gap={2} flex="1" minW={0}>
        {nodes.map((child) => (
          <Chip
            key={child.key}
            node={child}
            value={value}
            clearKey={clearKey}
            onChange={onChange}
            loading={loading}
          />
        ))}
      </Wrap>
    </HStack>
  );
}

export function FilterBar<TKey extends string>({
  label,
  options,
  value,
  clearKey,
  onChange,
  loading = false,
}: {
  /** Names what the chips act on. Rendered beside them, as a control label. */
  label?: string;
  options: ReadonlyArray<FilterNode<TKey>>;
  value: TKey;
  /** The value meaning "no filter", reached by clicking the active chip again. */
  clearKey: TKey;
  onChange: (key: TKey) => void;
  /**
   * The rows are still coming. The chips are drawn at once, so the page does
   * not grow its filters after the table, with the counts as skeletons.
   */
  loading?: boolean;
}) {
  const branching = options.filter((option) => (option.children ?? []).length > 0);

  // With one branching option the top level fits on a single row and its
  // children hang beneath it unambiguously. With several, each needs its own
  // line, or two elbow rows would sit at the same indent under a shared row and
  // neither would obviously own its children.
  const singleBranch = branching.length <= 1;

  return (
    <HStack align="flex-start" gap={4}>
      {label === undefined ? null : (
        <Text
          fontSize="xs"
          fontWeight="semibold"
          textTransform="uppercase"
          letterSpacing="0.18em"
          color="fg.muted"
          flexShrink={0}
          // Aligned with the text inside the first chip rather than its box.
          pt="9px"
        >
          {label}
        </Text>
      )}

      <Stack gap={3} align="stretch" flex="1" minW={0}>
        {singleBranch ? (
          <>
            <Wrap gap={2}>
              {options.map((option) => (
                <Chip
                  key={option.key}
                  node={option}
                  value={value}
                  clearKey={clearKey}
                  onChange={onChange}
                  loading={loading}
                />
              ))}
            </Wrap>
            {branching.map((option) => (
              <ChildRow
                key={option.key}
                nodes={option.children ?? []}
                value={value}
                clearKey={clearKey}
                onChange={onChange}
                loading={loading}
              />
            ))}
          </>
        ) : (
          options.map((option) => (
            <Stack key={option.key} gap={3} align="stretch">
              <Wrap gap={2}>
                <Chip node={option} value={value} clearKey={clearKey} onChange={onChange} loading={loading} />
              </Wrap>
              {(option.children ?? []).length > 0 ? (
                <ChildRow
                  nodes={option.children ?? []}
                  value={value}
                  clearKey={clearKey}
                  onChange={onChange}
                  loading={loading}
                />
              ) : null}
            </Stack>
          ))
        )}
      </Stack>
    </HStack>
  );
}
